import { getLocalizedArray, t } from './localization.ts';
import { setNavigationBlocked } from './slides.ts';
import { markProgressStep, ProgressStep } from './progress.ts';
import { trackEvent } from './analytics.ts';
import type { HookedStage, HookedStageId } from './types.ts';

const STAGE_PROGRESS_STEP: Record<HookedStageId, string> = {
  trigger: ProgressStep.HOOKED_STAGE_TRIGGER,
  action: ProgressStep.HOOKED_STAGE_ACTION,
  'variable-reward': ProgressStep.HOOKED_STAGE_REWARD,
  investment: ProgressStep.HOOKED_STAGE_INVESTMENT,
};

const FALLBACK_HOOKED_STAGES: HookedStage[] = [
  {
    id: 'trigger',
    title: 'Trigger',
    summary: 'Cue that prompts you to act',
    examples: ['Push notification', 'Inbox badge', 'Boredom scroll'],
  },
  {
    id: 'action',
    title: 'Action',
    summary: 'Simple behavior in anticipation of reward',
    examples: ['Open the app', 'Swipe to refresh', 'Tap the play button'],
  },
  {
    id: 'variable-reward',
    title: 'Variable Reward',
    summary: 'Unpredictable payoff that hooks attention',
    examples: ['New likes or comments', 'Loot drop', 'Fresh content'],
  },
  {
    id: 'investment',
    title: 'Investment',
    summary: 'Effort that increases the product’s future value',
    examples: ['Upload a photo', 'Follow friends', 'Save preferences'],
  },
];

function normalizeStages(raw: unknown): HookedStage[] {
  if (!Array.isArray(raw) || !raw.length) {
    return FALLBACK_HOOKED_STAGES;
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const { id, title, summary, examples } = item;
      if (!id || typeof id !== 'string') return null;
      const safeTitle = typeof title === 'string' ? title : '';
      const safeSummary = typeof summary === 'string' ? summary : '';
      const safeExamples =
        Array.isArray(examples) && examples.length
          ? examples.filter((example) => typeof example === 'string')
          : [];
      return {
        id,
        title: safeTitle || FALLBACK_HOOKED_STAGES.find((stage) => stage.id === id)?.title || id,
        summary:
          safeSummary || FALLBACK_HOOKED_STAGES.find((stage) => stage.id === id)?.summary || '',
        examples: safeExamples.length
          ? safeExamples
          : FALLBACK_HOOKED_STAGES.find((stage) => stage.id === id)?.examples || [],
      };
    })
    .filter((item): item is HookedStage => item !== null);
}

function loadHookedStages(): HookedStage[] {
  const localized = getLocalizedArray('slides.hooked.stages', FALLBACK_HOOKED_STAGES);
  const normalised = normalizeStages(localized);
  return normalised.length ? normalised : FALLBACK_HOOKED_STAGES;
}

function getSlotLabel(index: number): string {
  return t(`slides.hooked.slotLabels.${index}`, `Stage ${index + 1} slot`);
}

function shuffle<T>(items: T[]): T[] {
  const list = items.slice();
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

function initHooked(): void {
  const root = document.querySelector('[data-hooked-exercise]');
  if (!root) {
    return;
  }

  const docEl = document.documentElement;
  const hookedStages = loadHookedStages();
  const slotButtons = Array.from(root.querySelectorAll('[data-slot]'));
  const overlay = root.querySelector('[data-stage-overlay]');
  const picker = overlay?.querySelector('.hooked-picker');
  const stageList = overlay?.querySelector('[data-stage-list]');
  const closeButton = overlay?.querySelector('[data-stage-close]');
  const videoTrigger = root.parentElement?.querySelector('[data-video-trigger]');
  const videoOverlay = root.parentElement?.querySelector('[data-video-overlay]');
  const videoCloseBtn = videoOverlay?.querySelector('[data-video-close]');

  if (!slotButtons.length || !overlay || !picker || !stageList) {
    return;
  }

  const assignments: (HookedStageId | null)[] = new Array(slotButtons.length).fill(null);
  let activeSlotIndex: number | null = null;
  let lastActiveElement: HTMLElement | null = null;
  let overlayOpen: boolean = false;
  const completedStageSteps = new Set<string>();

  function findStage(stageId: HookedStageId): HookedStage | null {
    return hookedStages.find((stage) => stage.id === stageId) || null;
  }

  function updateSlotDisplay(slotIndex: number): void {
    const slot = slotButtons[slotIndex];
    if (!slot) return;

    const stageId = assignments[slotIndex];
    const valueEl = slot.querySelector('[data-slot-value]');
    const descEl = slot.querySelector('[data-slot-desc]');
    const correctStage =
      ((slot as HTMLElement).dataset.correctStage as HookedStageId | undefined) || null;

    if (!stageId) {
      slot.classList.remove('is-filled');
      slot.removeAttribute('data-stage');
      slot.classList.remove('is-correct', 'is-incorrect');
      (slot as HTMLButtonElement).disabled = false;
      slot.removeAttribute('aria-disabled');
      valueEl!.textContent = getSlotLabel(slotIndex);
      if (descEl) {
        descEl.textContent = '';
      }
      return;
    }

    const stage = findStage(stageId);
    if (!stage) {
      return;
    }

    slot.classList.add('is-filled');
    slot.setAttribute('data-stage', stage.id);
    const isCorrect = correctStage && stage.id === correctStage;
    slot.classList.toggle('is-correct', Boolean(isCorrect));
    slot.classList.toggle('is-incorrect', !isCorrect);
    if (isCorrect) {
      (slot as HTMLButtonElement).disabled = true;
      slot.setAttribute('aria-disabled', 'true');
      const progressStep = STAGE_PROGRESS_STEP[correctStage as HookedStageId];
      if (progressStep && !completedStageSteps.has(progressStep)) {
        completedStageSteps.add(progressStep);
        markProgressStep(progressStep as ProgressStepId);
      }
    } else {
      (slot as HTMLButtonElement).disabled = false;
      slot.removeAttribute('aria-disabled');
    }
    valueEl!.textContent = stage.title;
    if (descEl) {
      descEl.textContent = '';
    }

    updateVideoTriggerState();
  }

  function closePicker(): void {
    if (!overlay) return;
    overlay.classList.remove('is-active');
    (overlay as HTMLElement).hidden = true;
    if (overlayOpen) {
      document.body.classList.remove('hooked-modal-open', 'slide-panel-open');
      docEl.classList.remove('hooked-modal-open', 'slide-panel-open');
      setNavigationBlocked(false);
      overlayOpen = false;
    }
    activeSlotIndex = null;
    slotButtons.forEach((slot) => slot.classList.remove('is-active'));
    if (lastActiveElement) {
      lastActiveElement.focus({ preventScroll: true });
      lastActiveElement = null;
    }
  }

  function allSlotsCorrect(): boolean {
    return assignments.every((stageId, idx) => {
      if (!stageId) return false;
      const slot = slotButtons[idx];
      return slot?.classList.contains('is-correct');
    });
  }

  function updateVideoTriggerState(): void {
    if (!videoTrigger) {
      return;
    }
    const unlocked = allSlotsCorrect();
    (videoTrigger as HTMLButtonElement).disabled = !unlocked;
    if (unlocked) {
      videoTrigger.removeAttribute('aria-disabled');
      trackEvent('exercise:hooked_complete');
    } else {
      videoTrigger.setAttribute('aria-disabled', 'true');
    }
  }

  function handleStageSelection(stageId: HookedStageId): void {
    if (activeSlotIndex === null) {
      return;
    }

    const existingIndex = assignments.findIndex(
      (assigned, idx) => assigned === stageId && idx !== activeSlotIndex
    );
    if (existingIndex !== -1) {
      const existingSlot = slotButtons[existingIndex];
      if (existingSlot?.classList.contains('is-correct')) {
        closePicker();
        return;
      }
      assignments[existingIndex] = null;
      updateSlotDisplay(existingIndex);
    }

    assignments[activeSlotIndex] = stageId;
    updateSlotDisplay(activeSlotIndex);

    // Track stage selection
    const correctStage = (slotButtons[activeSlotIndex] as HTMLElement)?.dataset.correctStage as
      | HookedStageId
      | undefined;
    const isCorrect = correctStage && stageId === correctStage;
    trackEvent('exercise:stage_select', {
      stage_id: stageId,
      slot_index: activeSlotIndex,
      is_correct: isCorrect,
    });

    closePicker();
  }

  function renderStageList(): void {
    const activeStageId = activeSlotIndex !== null ? assignments[activeSlotIndex] : null;
    const lockedStages = new Set<HookedStageId>();
    const usedElsewhere = new Set<HookedStageId>();

    assignments.forEach((stageId, idx) => {
      if (!stageId || idx === activeSlotIndex) {
        return;
      }
      const slot = slotButtons[idx];
      if (slot?.classList.contains('is-correct')) {
        lockedStages.add(stageId);
        return;
      }
      usedElsewhere.add(stageId);
    });

    const available: Array<{ stage: HookedStage; isUsed: boolean; isCurrent: boolean }> = [];
    const used: Array<{ stage: HookedStage; isUsed: boolean; isCurrent: boolean }> = [];

    hookedStages.forEach((stage) => {
      if (lockedStages.has(stage.id)) {
        return;
      }
      const isCurrent = stage.id === activeStageId;
      const isUsed = usedElsewhere.has(stage.id);
      const bucket = isUsed && !isCurrent ? used : available;
      bucket.push({ stage, isUsed, isCurrent });
    });

    const ordered = [...shuffle(available), ...shuffle(used)];

    stageList!.innerHTML = '';
    ordered.forEach(({ stage, isUsed, isCurrent }) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hooked-stage-option';
      if (isUsed) {
        button.classList.add('is-used');
      }
      if (isCurrent) {
        button.classList.add('is-current');
      }
      button.setAttribute('data-stage', stage.id);
      button.innerHTML = `
         <span class="hooked-stage-option__title">${stage.title}</span>
         <span class="hooked-stage-option__summary">${stage.summary}</span>
         <span class="hooked-stage-option__examples">${stage.examples.join(', ')}</span>
       `;
      button.addEventListener('click', () => {
        handleStageSelection(stage.id);
      });
      li.appendChild(button);
      stageList!.appendChild(li);
    });
  }

  function openPickerForSlot(slotIndex: number): void {
    if (activeSlotIndex === slotIndex) {
      closePicker();
      return;
    }
    activeSlotIndex = slotIndex;
    slotButtons.forEach((slot, idx) => {
      slot.classList.toggle('is-active', idx === slotIndex);
    });
    renderStageList();
    (overlay as HTMLElement).hidden = false;
    overlay!.classList.add('is-active');
    document.body.classList.add('hooked-modal-open', 'slide-panel-open');
    docEl.classList.add('hooked-modal-open', 'slide-panel-open');
    setNavigationBlocked(true);
    overlayOpen = true;
    lastActiveElement = slotButtons[slotIndex] as HTMLElement;

    requestAnimationFrame(() => {
      const firstOption = stageList!.querySelector('.hooked-stage-option');
      (firstOption as HTMLElement)?.focus();
    });
  }

  slotButtons.forEach((slot, index) => {
    slot.addEventListener('click', () => {
      if ((slot as HTMLButtonElement).disabled || slot.classList.contains('is-correct')) {
        return;
      }
      openPickerForSlot(index);
    });
  });

  function closeVideoOverlay(): void {
    if (!videoOverlay) return;
    videoOverlay.classList.remove('is-active');
    (videoOverlay as HTMLElement).hidden = true;
    document.body.classList.remove('hooked-modal-open', 'slide-panel-open');
    docEl.classList.remove('hooked-modal-open', 'slide-panel-open');
    setNavigationBlocked(false);
  }

  if (videoTrigger && videoOverlay) {
    videoTrigger.addEventListener('click', () => {
      if ((videoTrigger as HTMLButtonElement).disabled) {
        return;
      }
      markProgressStep(ProgressStep.HOOKED_VIDEO);
      trackEvent('video:view_start');
      (videoOverlay as HTMLElement).hidden = false;
      videoOverlay.classList.add('is-active');
      document.body.classList.add('hooked-modal-open', 'slide-panel-open');
      docEl.classList.add('hooked-modal-open', 'slide-panel-open');
      setNavigationBlocked(true);
      (
        videoOverlay.querySelector('button, [href], [tabindex]:not([tabindex="-1"])') as HTMLElement
      )?.focus();
    });

    if (videoCloseBtn) {
      videoCloseBtn.addEventListener('click', () => {
        closeVideoOverlay();
      });
    }

    videoOverlay.addEventListener('click', (event) => {
      if (event.target === videoOverlay) {
        closeVideoOverlay();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !(videoOverlay as HTMLElement).hidden) {
        closeVideoOverlay();
      }
    });
  }

  if (closeButton) {
    closeButton.addEventListener('click', () => {
      closePicker();
    });
  }

  if (overlay) {
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        closePicker();
      }
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!root.contains(event.target as Node)) {
        closePicker();
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closePicker();
    }
  });

  updateVideoTriggerState();
}

export { initHooked };
